import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Users as UsersIcon,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  accountAllocationErrorMessage,
  tharsternFailureReasonText,
} from "@shared-core/data/api/account-allocation";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import {
  allocationReasonLabel,
  suggestionBandLabel,
  type SuggestionBand,
} from "@shared-core/domain/allocationReason";
import type {
  AdminUserItem,
  AllocationClaimRow,
  BulkReallocationPreview,
  BulkReallocationResultRow,
  ClaimStatus,
  ReallocationQueueRow,
} from "@shared-core/data/adapter";
import { paths } from "@shared-core/routing/routes";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { Checkbox } from "../app/components/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TabsTriggerCount,
} from "../app/components/primitives/tabs";
import { ListPagination } from "../app/components/patterns/ListPagination";

const PAGE_SIZE = 20;
const BULK_CAP = 200;

function formatRelativeDays(days: number | null): string {
  if (days == null) return "—";
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

function BandBadge({ band }: { band: SuggestionBand }) {
  const styles: Record<SuggestionBand, string> = {
    strong: "bg-emerald-100 text-emerald-800",
    weak: "bg-amber-100 text-amber-800",
    none: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[band]}`}
    >
      {suggestionBandLabel(band)}
    </span>
  );
}

function ReasonCell({ row }: { row: ReallocationQueueRow }) {
  // The backend's reason classification is defensively nullable on the
  // wire; treat an absent reason the same as "unrecognised" rather than
  // assume a value the classifier never actually omits in practice.
  const reason = row.reason ?? "unrecognised";
  const label = allocationReasonLabel(reason, {
    leaverName: reason === "left" ? (row.raw_ref2 ?? undefined) : undefined,
  });
  const secondary =
    reason === "bucket"
      ? row.raw_ref2
      : reason === "unrecognised"
        ? row.raw_ref2
        : null;
  return (
    <div>
      <div className="text-sm">{label}</div>
      {secondary && (
        <div className="text-xs text-muted-foreground">{secondary}</div>
      )}
    </div>
  );
}

function SuggestionCell({ row }: { row: ReallocationQueueRow }) {
  const s = row.suggestion;
  if (!s || s.band === "none" || !s.suggested_user_name) {
    return <span className="text-sm text-muted-foreground">No suggestion</span>;
  }
  const top = s.evidence[0];
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium">{s.suggested_user_name}</span>
        <BandBadge band={s.band} />
      </div>
      {top && (
        <div className="text-xs text-muted-foreground">
          made {top.jobs} of {s.total_jobs} bookings
        </div>
      )}
    </div>
  );
}

type RowBanner = { kind: "updating" } | { kind: "error"; message: string };

/**
 * Per-row bulk-accept outcome copy — the real backend's full
 * `BulkRowResultResponse.outcome` vocabulary (account_allocation_module's
 * `bulk_reallocate` action). "written" rows aren't listed individually;
 * every skip/fail outcome gets its own row so a manager can see exactly
 * which customers didn't go through and why, rather than just a count.
 */
function bulkRowOutcomeLabel(row: BulkReallocationResultRow): string | null {
  switch (row.outcome) {
    case "written":
      return null;
    case "skipped_stale_suggestion":
      return "Suggestion changed — review this one";
    case "skipped_not_in_queue":
      return "No longer needs a CSR";
    case "skipped_target_ineligible":
      return "Target CSR isn't eligible";
    case "skipped_name_unresolvable":
      return "Can't assign until the Tharstern user is fixed";
    case "skipped_idempotent":
      return "Already assigned";
    case "failed":
      return "Tharstern didn't accept the change";
    default:
      return row.outcome;
  }
}

export function AccountAllocationPage() {
  const adapter = useDataAdapter();
  const [tab, setTab] = useState<"queue" | "claims">("queue");

  // ----- Shared CSR roster (for the "Suggested CSR" filter + the
  // "Assign to someone else" picker) — current (not-left) CSRs only. -----
  const [csrs, setCsrs] = useState<AdminUserItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    adapter.getAdminUsers({ role: "csr", pageSize: 200 }).then((res) => {
      if (!cancelled) setCsrs(res.items.filter((u) => !u.left_at));
    });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  // ----- Summary tile (also used for the header count) -----
  const [summary, setSummary] = useState<{
    unallocated_total: number;
    pending_claims_count: number;
  } | null>(null);
  const loadSummary = useCallback(() => {
    adapter.getAllocationSummary().then(setSummary);
  }, [adapter]);
  useEffect(loadSummary, [loadSummary]);

  // ----- Queue tab state -----
  const [suggestedUserId, setSuggestedUserId] = useState<string>("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [sort, setSort] = useState("-jobs_12m");
  const [page, setPage] = useState(1);

  const [queue, setQueue] = useState<{
    items: ReallocationQueueRow[];
    total: number;
    totalPages: number;
  } | null>(null);
  const [queueLoading, setQueueLoading] = useState(true);
  // A failed load must not fall through to the "Every account has a CSR"
  // empty state — that would tell a manager the backlog is clear when we
  // simply couldn't read it.
  const [queueError, setQueueError] = useState(false);

  const loadQueue = useCallback(() => {
    setQueueLoading(true);
    setQueueError(false);
    adapter
      .getAccountAllocationQueue({
        suggestedUserId: suggestedUserId || undefined,
        q: debouncedSearch || undefined,
        sort,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((res) => {
        setQueue({
          items: res.items,
          total: res.total,
          totalPages: res.totalPages,
        });
      })
      .catch(() => {
        setQueue(null);
        setQueueError(true);
      })
      .finally(() => setQueueLoading(false));
  }, [adapter, suggestedUserId, debouncedSearch, sort, page]);

  useEffect(() => {
    setPage(1);
  }, [suggestedUserId, debouncedSearch, sort]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // ----- Per-row transient action state -----
  const [rowBanners, setRowBanners] = useState<Record<string, RowBanner>>({});
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const setPending = (customerId: string, pending: boolean) =>
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(customerId);
      else next.delete(customerId);
      return next;
    });

  async function assign(row: ReallocationQueueRow, toUserId: string) {
    setPending(row.customer_id, true);
    setRowBanners((prev) => {
      const { [row.customer_id]: _drop, ...rest } = prev;
      return rest;
    });
    try {
      await adapter.reallocateCustomer(row.customer_id, toUserId);
      toast.success(`${row.customer_name} assigned — will show within 2 hours`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.customer_id);
        return next;
      });
      loadQueue();
      loadSummary();
    } catch (err) {
      const message = accountAllocationErrorMessage(err);
      setRowBanners((prev) => ({
        ...prev,
        [row.customer_id]: { kind: "error", message },
      }));
      toast.error(message);
      // A Tharstern-write failure (as opposed to a precondition failure)
      // persists a `failed` reallocation server-side — reload so the row's
      // own reallocation_state carries the retry affordance from here on.
      loadQueue();
    } finally {
      setPending(row.customer_id, false);
    }
  }

  async function retry(row: ReallocationQueueRow) {
    if (!row.reallocation_state) return;
    setPending(row.customer_id, true);
    try {
      await adapter.retryFailedReallocation(
        row.reallocation_state.reallocation_id,
      );
      toast.success(`${row.customer_name} assigned — will show within 2 hours`);
      loadQueue();
      loadSummary();
    } catch (err) {
      toast.error(accountAllocationErrorMessage(err));
      loadQueue();
    } finally {
      setPending(row.customer_id, false);
    }
  }

  // ----- Assign-to-someone-else dialog -----
  const [overrideRow, setOverrideRow] = useState<ReallocationQueueRow | null>(
    null,
  );
  const [overrideTargetId, setOverrideTargetId] = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  async function confirmOverride() {
    if (!overrideRow || !overrideTargetId) return;
    setOverrideSubmitting(true);
    try {
      await assign(overrideRow, overrideTargetId);
      setOverrideRow(null);
      setOverrideTargetId("");
    } finally {
      setOverrideSubmitting(false);
    }
  }

  // ----- Row selection + bulk accept -----
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const items = useMemo(() => queue?.items ?? [], [queue]);
  const allOnPageSelected =
    items.length > 0 && items.every((r) => selectedIds.has(r.customer_id));

  function toggleAll() {
    setSelectedIds((prev) => {
      if (allOnPageSelected) {
        const next = new Set(prev);
        for (const r of items) next.delete(r.customer_id);
        return next;
      }
      const next = new Set(prev);
      for (const r of items) next.add(r.customer_id);
      return next;
    });
  }
  function toggleRow(customerId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  }

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPreview, setBulkPreview] =
    useState<BulkReallocationPreview | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // Non-null once a submit has returned — the dialog switches from the
  // preview (per-CSR load) to the per-row outcome breakdown and stays open
  // until the manager dismisses it, rather than auto-closing over a skip or
  // failure a plain toast would bury.
  const [bulkResultRows, setBulkResultRows] = useState<
    BulkReallocationResultRow[] | null
  >(null);
  // The queue page is the only place a customer_name is available for a
  // bulk row (the preview/result responses carry ids only). Snapshotted at
  // submit: the post-write queue reload drops every row that was written,
  // so a live lookup would show raw ids for exactly the successful rows.
  const [bulkResultNames, setBulkResultNames] = useState<Map<string, string>>(
    new Map(),
  );

  async function openBulkDialog() {
    setBulkOpen(true);
    setBulkLoading(true);
    setBulkPreview(null);
    setBulkResultRows(null);
    try {
      const preview = await adapter.getBulkReallocationPreview({
        customerIds: [...selectedIds],
      });
      setBulkPreview(preview);
    } finally {
      setBulkLoading(false);
    }
  }

  async function confirmBulk() {
    if (!bulkPreview) return;
    setBulkSubmitting(true);
    setBulkResultNames(
      new Map(items.map((r) => [r.customer_id, r.customer_name])),
    );
    try {
      const result = await adapter.bulkReallocate({
        mode: bulkPreview.mode,
        items: bulkPreview.items,
      });
      if (result.failed > 0 || result.skipped > 0) {
        toast.error(
          `${result.written} assigned, ${result.failed} failed, ${result.skipped} skipped`,
        );
        setBulkResultRows(result.rows);
      } else {
        toast.success(`${result.written} customer(s) assigned`);
        setBulkOpen(false);
      }
      setSelectedIds(new Set());
      loadQueue();
      loadSummary();
    } finally {
      setBulkSubmitting(false);
    }
  }

  // ----- Claims tab -----
  const [claimStatus, setClaimStatus] = useState<ClaimStatus>("pending");
  const [claims, setClaims] = useState<AllocationClaimRow[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [claimsError, setClaimsError] = useState(false);
  const loadClaims = useCallback(() => {
    setClaimsLoading(true);
    setClaimsError(false);
    adapter
      .getAllocationClaims({ status: [claimStatus], pageSize: 100 })
      .then((res) => setClaims(res.items))
      .catch(() => {
        setClaims([]);
        setClaimsError(true);
      })
      .finally(() => setClaimsLoading(false));
  }, [adapter, claimStatus]);
  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  // Self-approval guard (M2) — mirrors the backend's own check. The mock
  // session's fixed current user is "user_001" ("Admin User").
  const currentUserId = "user_001";

  const [approving, setApproving] = useState<string | null>(null);
  async function approve(claim: AllocationClaimRow) {
    setApproving(claim.claim_id);
    try {
      await adapter.approveClaim(claim.claim_id);
      toast.success(
        `${claim.customer_name} assigned to ${claim.claimant_name}`,
      );
      loadClaims();
      loadQueue();
      loadSummary();
    } catch (err) {
      toast.error(accountAllocationErrorMessage(err));
    } finally {
      setApproving(null);
    }
  }

  const [declineTarget, setDeclineTarget] = useState<AllocationClaimRow | null>(
    null,
  );
  const [declineNote, setDeclineNote] = useState("");
  const [declining, setDeclining] = useState(false);
  async function confirmDecline() {
    if (!declineTarget) return;
    setDeclining(true);
    try {
      await adapter.declineClaim(
        declineTarget.claim_id,
        declineNote || undefined,
      );
      toast.success(`Claim declined`);
      setDeclineTarget(null);
      setDeclineNote("");
      loadClaims();
    } finally {
      setDeclining(false);
    }
  }

  const eligibleSelectedCount = useMemo(
    () =>
      items.filter(
        (r) =>
          selectedIds.has(r.customer_id) &&
          r.suggestion &&
          r.suggestion.band !== "none",
      ).length,
    [items, selectedIds],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <UsersIcon className="size-6" /> Account allocation
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {summary
            ? `${summary.unallocated_total.toLocaleString()} ${
                summary.unallocated_total === 1
                  ? "account needs"
                  : "accounts need"
              } a CSR`
            : "Loading…"}
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "queue" | "claims")}>
        <TabsList variant="underline">
          <TabsTrigger value="queue">
            <span className="inline-flex items-center gap-1.5">
              Needs a CSR
              {queue && <TabsTriggerCount>{queue.total}</TabsTriggerCount>}
            </span>
          </TabsTrigger>
          <TabsTrigger value="claims">
            <span className="inline-flex items-center gap-1.5">
              Claims
              {summary && (
                <TabsTriggerCount>
                  {summary.pending_claims_count}
                </TabsTriggerCount>
              )}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={suggestedUserId}
              onChange={(e) => setSuggestedUserId(e.target.value)}
              className="border border-input rounded-md px-2 py-1.5 bg-background text-sm"
              aria-label="Filter by suggested CSR"
            >
              <option value="">All suggested CSRs</option>
              {csrs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Search customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-input rounded-md px-3 py-1.5 bg-background text-sm max-w-xs"
              aria-label="Search customer"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-input rounded-md px-2 py-1.5 bg-background text-sm ml-auto"
              aria-label="Sort"
            >
              <option value="-jobs_12m">Most bookings (12m)</option>
              <option value="last_ordered">Last ordered (longest ago)</option>
              <option value="-last_ordered">Last ordered (most recent)</option>
              <option value="customer_name">Customer name</option>
            </select>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
              <span className="text-sm">
                {selectedIds.size} selected
                {eligibleSelectedCount < selectedIds.size &&
                  ` (${eligibleSelectedCount} with a suggestion)`}
              </span>
              <Button
                size="sm"
                onClick={openBulkDialog}
                disabled={eligibleSelectedCount === 0}
              >
                Accept selected suggestions ({eligibleSelectedCount})
              </Button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear selection
              </button>
            </div>
          )}

          {queueLoading ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              Loading…
            </Card>
          ) : queueError ? (
            <Card className="p-8 text-center" role="alert">
              <AlertTriangle className="size-8 mx-auto text-destructive mb-2" />
              <p className="font-medium">Couldn&apos;t load the queue</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={loadQueue}
              >
                Try again
              </Button>
            </Card>
          ) : items.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle2 className="size-8 mx-auto text-emerald-500 mb-2" />
              <p className="font-medium">Every account has a CSR</p>
              <p className="text-sm text-muted-foreground mt-1">
                Nothing matches the current filters.
              </p>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-hover text-left">
                    <tr>
                      <th className="p-2 w-8">
                        <Checkbox
                          checked={allOnPageSelected}
                          onCheckedChange={toggleAll}
                          aria-label="Select all on this page"
                        />
                      </th>
                      <th className="p-2 font-medium">Customer</th>
                      <th className="p-2 font-medium">Why it&apos;s here</th>
                      <th className="p-2 font-medium">Last ordered</th>
                      <th className="p-2 font-medium">Bookings (12 months)</th>
                      <th className="p-2 font-medium">Suggested CSR</th>
                      <th className="p-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const banner = rowBanners[row.customer_id];
                      const isPending = pendingIds.has(row.customer_id);
                      const failedState =
                        row.reallocation_state?.write_status === "failed"
                          ? row.reallocation_state
                          : null;
                      return (
                        <tr
                          key={row.customer_id}
                          className="border-t border-border align-top hover:bg-muted/30"
                        >
                          <td className="p-2">
                            <Checkbox
                              checked={selectedIds.has(row.customer_id)}
                              onCheckedChange={() => toggleRow(row.customer_id)}
                              aria-label={`Select ${row.customer_name}`}
                            />
                          </td>
                          <td className="p-2">
                            <Link
                              to={paths.customerDetail({
                                customerId: row.customer_id,
                              })}
                              className="font-medium hover:underline"
                            >
                              {row.customer_name}
                            </Link>
                            {row.pending_claim && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                Claimed by {row.pending_claim.claimant_name}
                              </div>
                            )}
                          </td>
                          <td className="p-2">
                            <ReasonCell row={row} />
                          </td>
                          <td className="p-2 whitespace-nowrap text-muted-foreground">
                            {formatRelativeDays(row.days_since_last_order)}
                          </td>
                          <td className="p-2 tabular-nums">
                            {row.jobs_12m ?? "—"}
                          </td>
                          <td className="p-2">
                            <SuggestionCell row={row} />
                          </td>
                          <td className="p-2 text-right whitespace-nowrap">
                            {isPending ? (
                              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                <RefreshCw className="size-3 animate-spin" />
                                Updating Tharstern…
                              </span>
                            ) : failedState ? (
                              <div className="flex flex-col items-end gap-1">
                                <span className="text-xs text-destructive inline-flex items-center gap-1">
                                  <AlertTriangle className="size-3" />
                                  Tharstern didn&apos;t accept the change
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {tharsternFailureReasonText(
                                    failedState.failure_reason_code,
                                  )}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => retry(row)}
                                >
                                  Try again
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                {row.suggestion?.suggested_user_id &&
                                  row.suggestion.band !== "none" && (
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        assign(
                                          row,
                                          row.suggestion!.suggested_user_id!,
                                        )
                                      }
                                    >
                                      Assign
                                    </Button>
                                  )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setOverrideRow(row);
                                    setOverrideTargetId("");
                                  }}
                                >
                                  Assign to someone else
                                </Button>
                              </div>
                            )}
                            {banner?.kind === "error" && !failedState && (
                              <p className="text-xs text-destructive mt-1 max-w-[220px] text-right">
                                {banner.message}
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {queue && queue.totalPages > 1 && (
            <ListPagination
              page={page}
              totalPages={queue.totalPages}
              onPageChange={setPage}
            />
          )}
        </TabsContent>

        <TabsContent value="claims" className="space-y-4 mt-4">
          <div className="flex items-center gap-1.5">
            {(
              ["pending", "approved", "declined", "withdrawn"] as ClaimStatus[]
            ).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setClaimStatus(s)}
                className={`px-2.5 py-1 rounded text-xs border capitalize transition-colors ${
                  claimStatus === s
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {claimsLoading ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              Loading…
            </Card>
          ) : claimsError ? (
            <Card className="p-8 text-center" role="alert">
              <AlertTriangle className="size-8 mx-auto text-destructive mb-2" />
              <p className="font-medium">Couldn&apos;t load claims</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={loadClaims}
              >
                Try again
              </Button>
            </Card>
          ) : claims.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle2 className="size-8 mx-auto text-emerald-500 mb-2" />
              <p className="font-medium">No claims waiting</p>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-hover text-left">
                    <tr>
                      <th className="p-2 font-medium">Customer</th>
                      <th className="p-2 font-medium">Claimed by</th>
                      <th className="p-2 font-medium">When</th>
                      {claimStatus === "declined" && (
                        <th className="p-2 font-medium">Note</th>
                      )}
                      {claimStatus === "pending" && (
                        <th className="p-2 font-medium" />
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((claim) => {
                      const isSelfApproval =
                        claim.claimant_user_id === currentUserId;
                      return (
                        <tr
                          key={claim.claim_id}
                          className="border-t border-border"
                        >
                          <td className="p-2">
                            <Link
                              to={paths.customerDetail({
                                customerId: claim.customer_id,
                              })}
                              className="font-medium hover:underline"
                            >
                              {claim.customer_name}
                            </Link>
                          </td>
                          <td className="p-2">{claim.claimant_name}</td>
                          <td className="p-2 text-muted-foreground">
                            {formatShortDateTime(claim.created_at)}
                          </td>
                          {claimStatus === "declined" && (
                            <td className="p-2 text-muted-foreground max-w-sm">
                              {claim.note ?? "—"}
                            </td>
                          )}
                          {claimStatus === "pending" && (
                            <td className="p-2 text-right whitespace-nowrap">
                              <div className="inline-flex items-center gap-2">
                                <span
                                  title={
                                    isSelfApproval
                                      ? "You can't approve your own claim"
                                      : undefined
                                  }
                                >
                                  <Button
                                    size="sm"
                                    disabled={
                                      isSelfApproval ||
                                      approving === claim.claim_id
                                    }
                                    onClick={() => approve(claim)}
                                  >
                                    {approving === claim.claim_id
                                      ? "Approving…"
                                      : "Approve"}
                                  </Button>
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setDeclineTarget(claim)}
                                >
                                  Decline
                                </Button>
                              </div>
                              {isSelfApproval && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  You can&apos;t approve your own claim
                                </p>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Assign-to-someone-else dialog */}
      <Dialog
        open={overrideRow != null}
        onOpenChange={(open) => !open && setOverrideRow(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign to someone else</DialogTitle>
            <DialogDescription>{overrideRow?.customer_name}</DialogDescription>
          </DialogHeader>
          <select
            value={overrideTargetId}
            onChange={(e) => setOverrideTargetId(e.target.value)}
            className="w-full border border-input rounded-md px-3 py-2 bg-background text-sm"
            aria-label="Choose a CSR"
          >
            <option value="">Choose a CSR…</option>
            {csrs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideRow(null)}>
              Cancel
            </Button>
            <Button
              disabled={!overrideTargetId || overrideSubmitting}
              onClick={confirmOverride}
            >
              {overrideSubmitting ? "Assigning…" : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk accept confirm dialog */}
      <Dialog
        open={bulkOpen}
        onOpenChange={(open) => !open && setBulkOpen(false)}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {bulkResultRows
                ? "Bulk assign results"
                : "Accept selected suggestions"}
            </DialogTitle>
            {!bulkResultRows && (
              <DialogDescription>
                {bulkPreview
                  ? `${bulkPreview.count} customer${bulkPreview.count === 1 ? "" : "s"} will be reassigned.${
                      bulkPreview.truncated
                        ? ` Capped at ${BULK_CAP} — only the first ${BULK_CAP} will be applied.`
                        : ""
                    }${
                      bulkPreview.excluded_no_suggestion_count > 0
                        ? ` ${bulkPreview.excluded_no_suggestion_count} selected row(s) have no suggestion and are excluded.`
                        : ""
                    }`
                  : "Loading…"}
              </DialogDescription>
            )}
          </DialogHeader>
          {bulkResultRows ? (
            <div className="space-y-1.5">
              <ul className="text-sm space-y-1.5">
                {bulkResultRows.map((row) => {
                  const outcomeLabel = bulkRowOutcomeLabel(row);
                  return (
                    <li
                      key={row.customer_id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span>
                        {bulkResultNames.get(row.customer_id) ??
                          row.customer_id}
                      </span>
                      {outcomeLabel ? (
                        <span className="text-xs text-destructive text-right">
                          {outcomeLabel}
                          {row.outcome === "failed" && (
                            <span className="block text-muted-foreground">
                              {tharsternFailureReasonText(
                                row.failure_reason_code,
                              )}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-xs text-emerald-700">
                          Assigned
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : bulkLoading ? (
            <p className="text-sm text-muted-foreground">Loading preview…</p>
          ) : bulkPreview ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                Load per CSR
              </p>
              {bulkPreview.per_csr_load.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No eligible rows selected.
                </p>
              ) : (
                <ul className="text-sm space-y-1">
                  {bulkPreview.per_csr_load.map((c) => (
                    <li key={c.user_id} className="flex justify-between">
                      <span>{c.name}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {c.current_portfolio_size} → {c.new_portfolio_size} (+
                        {c.customers_gained})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
          <DialogFooter>
            {bulkResultRows ? (
              <Button onClick={() => setBulkOpen(false)}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    bulkLoading ||
                    bulkSubmitting ||
                    !bulkPreview ||
                    bulkPreview.items.length === 0
                  }
                  onClick={confirmBulk}
                >
                  {bulkSubmitting
                    ? "Assigning…"
                    : `Accept selected suggestions (${bulkPreview?.items.length ?? 0})`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline claim dialog */}
      <Dialog
        open={declineTarget != null}
        onOpenChange={(open) => !open && setDeclineTarget(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Decline claim</DialogTitle>
            <DialogDescription>
              {declineTarget?.customer_name} — claimed by{" "}
              {declineTarget?.claimant_name}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={declineNote}
            onChange={(e) => setDeclineNote(e.target.value)}
            placeholder="Reason (shown to the manager, not the claimant)"
            className="w-full border border-input rounded-md px-3 py-2 bg-background text-sm min-h-20"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineTarget(null)}>
              Cancel
            </Button>
            <Button disabled={declining} onClick={confirmDecline}>
              {declining ? "Declining…" : "Decline"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatShortDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}
